/* eslint-disable no-param-reassign */
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import ListCharactersResponse from 'api/response/ListCharactersResponse';
import { getTemplatesList } from 'api/services/AssetsService';
import {
	convertBase64ImageToFile,
	deleteCharacterService,
	generateCharacterService,
	getAllCharacters,
	getCharacterDescriptionService,
	updateCharacterService,
	uploadCharacterImageAssetService,
} from 'api/services/CharacterService';
import { AmplitudeEvents } from 'core/constants/AmplitudeEvents';
import { ApiStage } from 'core/constants/enums/ApiStages.ts';
import { RendernetErrors } from 'core/constants/enums/RendernetErrors.ts';
import { TemplateType } from 'core/constants/enums/TemplateType.ts';
import { ToastType } from 'core/constants/enums/ToastTypeEnum.ts';
import { PostHogEvents } from 'core/constants/PostHogEvents';
import { sendToast } from 'core/layouts/components/toast/ToastUtils';
import {
	createNewCharacterExtraReducers,
	createNewCharacterReducers,
} from 'core/store/reducers/characterStudioReducer/CreateNewCharacterActions';
import { captureAmplitudeEvent } from 'core/utils/AmplitudeUtils.ts';
import { LoggerUtil } from 'core/utils/loggerUtil.ts';
import { capturePosthogEvent } from 'core/utils/PosthogUtil.ts';
import {
	getImageDimensionsFromFileAsync,
	handleSomethingWentWrongErrorWithToast,
} from 'core/utils/utils';

const initialState = {
	selectedCharacterStudio: null,
	selectedCharacter: null,
	selectedCharacterHome: null,

	controlWeight: 0.5,
	realisticFaceSwap: true,

	controlWeightStudio: 0.5,
	realisticFaceSwapStudio: true,

	controlWeightHome: 0.5,
	realisticFaceSwapHome: true,

	characterCreationReferenceImage: null,
	isCharacterCreationInProgress: false,
	characterCreationError: null,
	characterCreationApiStage: ApiStage.IDLE,

	listCharactersApiStage: ApiStage.IDLE,
	listCharacters: [],
	myCharactersCurrentPage: 1,
	characterPopUpData: null,

	selectedEditCharacter: null,
	editCharacterApiStage: ApiStage.IDLE,

	deletedCharacters: {},
	deleteCharacterApiStage: ApiStage.IDLE,
	characterGenerationPrompt: '',
	characterGenerationData: { mediaId: null, thumbnail: null, mediaUrl: null, status: 'idle' },

	characterGenerateApiStage: ApiStage.IDLE,
	CharacterGenerationStatus: ApiStage.IDLE,

	characterTemplatesApiStage: ApiStage.IDLE,
	characterTemplates: [],

	characterDescriptionApiStage: ApiStage.IDLE,
	characterDescription: '',

	croppedCharacterImage: { id: null, url: null, uploadStatus: false },
};

export const charactersInitialState = initialState;

export const listCharacters = createAsyncThunk(
	'canvas/listCharacters',
	async ({ type, page, forceRefetch } = {}, { dispatch } = {}) =>
		getAllCharacters(type, page, undefined, { dispatch, forceRefetch }),
);

// export const listCharacters = createAsyncThunk(
// 	'canvas/listCharacters',
// 	async ({ type, page } = {}) => {
// 		return getAllCharacters(type, page);
// 	},
// );

export const generateCharacterAsync = createAsyncThunk(
	'characterStudio/generateCharacterAsync',
	async (data) => {
		const { characterPrompt } = data;
		return generateCharacterService(characterPrompt);
	},
);

export const editCharacterAsync = createAsyncThunk(
	'characterStudio/editCharacterAsync',
	async ({ characterData, characterImage, characterId, isSampleAssets }, { dispatch }) => {
		if (isSampleAssets) {
			return updateCharacterService({ ...characterData, image: characterImage }, characterId, {
				dispatch,
			});
		}
		return getImageDimensionsFromFileAsync(characterImage)
			.then((imageDimensions) =>
				convertBase64ImageToFile(characterImage).then((file) => ({ imageDimensions, file })),
			)
			.then((data) => {
				const { imageDimensions, file } = data;
				const [width, height] = imageDimensions;
				return uploadCharacterImageAssetService(file, {
					width,
					height,
					type: file.type,
				})
					.then(({ image }) => ({ ...characterData, image }))
					.catch((error) => {
						throw new Error(error?.message);
					});
			})
			.then((character) => updateCharacterService(character, characterId, { dispatch }));
	},
);
export const deleteCharacterAsync = createAsyncThunk(
	'characterStudio/deleteCharacter',
	async (characterId, { dispatch }) => deleteCharacterService([characterId], { dispatch }),
);

export const getCharacterDescriptionAsync = createAsyncThunk(
	'characterStudio/getCharacterDescription',
	async (assetId) => getCharacterDescriptionService(assetId),
);

export const getCharacterTemplatesAsync = createAsyncThunk(
	'characterStudio/getCharacterTemplates',
	async (_, { dispatch }) => {
		return getTemplatesList(TemplateType.CHARACTER, { dispatch });
	},
);

export const characterStudioSlice = createSlice({
	name: 'characterStudio',
	initialState,
	reducers: {
		setSelectedCharacterStudio: (state, action) => {
			state.controlWeightStudio = 0.5;
			state.realisticFaceSwapStudio = true;
			state.selectedCharacterStudio = action.payload;
		},
		setSelectedCharacter: (state, action) => {
			// Reset control weight and realistic face swap when a new character is selected
			state.controlWeight = 0.5;
			state.realisticFaceSwap = true;
			state.selectedCharacter = action.payload;
		},
		setSelectedCharacterHome: (state, action) => {
			state.controlWeightHome = 0.5;
			state.realisticFaceSwapHome = true;
			state.selectedCharacterHome = action.payload;
		},
		clearSelectedCharacterHome: (state) => {
			state.selectedCharacterHome = null;
		},
		clearSelectedCharacter: (state) => {
			state.selectedCharacter = null;
		},
		clearSelectedCharacterStudio: (state) => {
			state.selectedCharacterStudio = null;
		},
		setControlWeight: (state, action) => {
			state.controlWeight = action.payload;
		},
		setControlWeightStudio: (state, action) => {
			state.controlWeightStudio = action.payload;
		},
		setControlWeightHome: (state, action) => {
			state.controlWeightHome = action.payload;
		},
		toggleEnableRealisticFaceSwapStudio: (state, action) => {
			state.realisticFaceSwapStudio = action.payload;
		},
		toggleEnableRealisticFaceSwap: (state, action) => {
			state.realisticFaceSwap = action.payload;
		},
		toggleEnableRealisticFaceSwapHome: (state, action) => {
			state.realisticFaceSwapHome = action.payload;
		},
		setCharacterPopUpData: (state, action) => {
			state.characterPopUpData = action.payload;
		},
		setSelectedEditCharacter: (state, action) => {
			state.selectedEditCharacter = action.payload;
		},
		setCharacterGenerationPrompt: (state, action) => {
			state.characterGenerationPrompt = action.payload;
		},
		setCharacterGenerationData: (state, action) => {
			state.characterGenerationData = action.payload;
		},
		setCroppedCharacterImage: (state, action) => {
			state.croppedCharacterImage = action.payload;
		},
		setCharacterDescription: (state, action) => {
			state.characterDescription = action.payload;
		},
		setCharacterDescriptionApiStage: (state, action) => {
			state.characterDescriptionApiStage = action.payload;
		},
		resetCharacterState: (state) => {
			Object.keys(initialState).forEach((key) => {
				state[key] = initialState[key];
			});
		},
		setMyCharactersCurrentPage: (state, action) => {
			state.myCharactersCurrentPage = action.payload;
		},
		...createNewCharacterReducers,
	},
	extraReducers: (builder) => {
		builder.addCase(listCharacters.pending, (state, action) => {
			if (!action.meta.arg?.page || action.meta.arg?.page === 1) {
				state.listCharactersApiStage = ApiStage.LOADING;
			}
		});
		// builder.addCase(listCharacters.fulfilled, (state, action) => {
		// 	const charactersList = new ListCharactersResponse(action.payload.data).formatCharacterData();
		// 	const charactersListPage = action.payload.pagination?.page;
		// 	if (charactersListPage === 1) {
		// 		state.listCharacters = charactersList;
		// 		state.listCharactersApiStage = ApiStage.SUCCESS;
		// 	}
		//
		// 	state.myCharactersCurrentPage = charactersListPage;
		// 	window.dispatchEvent(
		// 		new CustomEvent('charactersPageUpdate', {
		// 			detail: {
		// 				charactersList,
		// 				charactersListPagination: action.payload.pagination,
		// 			},
		// 		}),
		// 	);
		// });

		builder.addCase('api/executeQuery/fulfilled', (state, action) => {
			if (action.meta.arg.endpointName === 'getCharacters') {
				// const { originalArgs } = action.meta.arg;
				const charactersList = new ListCharactersResponse(
					action.payload?.data,
				).formatCharacterData();
				const charactersListPage = action.payload.pagination?.page;
				if (charactersListPage === 1) {
					state.listCharacters = charactersList;
					state.listCharactersApiStage = ApiStage.SUCCESS;
				}
				state.myCharactersCurrentPage = charactersListPage;
				window.dispatchEvent(
					new CustomEvent('charactersPageUpdate', {
						detail: {
							charactersList,
							charactersListPagination: action.payload.pagination,
						},
					}),
				);
			}
		});

		builder.addCase(listCharacters.rejected, (state, action) => {
			if (!action.meta.arg?.page || action.meta.arg?.page === 1) {
				state.listCharactersApiStage = ApiStage.ERROR;
			}
			const { error } = action;
			handleSomethingWentWrongErrorWithToast('API request failed', error);
		});

		builder.addCase(editCharacterAsync.pending, (state) => {
			state.editCharacterApiStage = ApiStage.LOADING;
		});
		builder.addCase(editCharacterAsync.fulfilled, (state, action) => {
			const editedCharacter = new ListCharactersResponse(action.payload).formatCharacterData();
			const index = state.listCharacters.findIndex(
				(character) => character.id === editedCharacter.id,
			);
			if (index !== -1) {
				state.listCharacters = [
					...state.listCharacters.slice(0, index),
					editedCharacter,
					...state.listCharacters.slice(index + 1),
				];
			}
			state.editCharacterApiStage = ApiStage.SUCCESS;
		});
		builder.addCase(editCharacterAsync.rejected, (state, action) => {
			state.editCharacterApiStage = ApiStage.ERROR;
			const { error } = action;
			if (error.code === RendernetErrors.NSFW_PROMPT) {
				sendToast(ToastType.NSFW_CHARACTER_PROMPT_DESCRIPTION);
			} else {
				handleSomethingWentWrongErrorWithToast('API request failed', error);
			}
		});

		builder.addCase(deleteCharacterAsync.pending, (state) => {
			state.deleteCharacterApiStage = ApiStage.LOADING;
		});
		builder.addCase(deleteCharacterAsync.fulfilled, (state, action) => {
			state.deletedCharacters = action.payload;
			state.listCharacters = state.listCharacters.filter(
				(character) => action.payload[character.id] !== 'archive',
			);
			state.deleteCharacterApiStage = ApiStage.SUCCESS;
		});
		builder.addCase(deleteCharacterAsync.rejected, (state, action) => {
			state.deleteCharacterApiStage = ApiStage.ERROR;
			const { error } = action;
			handleSomethingWentWrongErrorWithToast('API request failed', error);
		});

		// character generation
		builder.addCase(generateCharacterAsync.pending, (state) => {
			state.characterGenerateApiStage = ApiStage.LOADING;
			state.characterGenerationData = {
				...state.characterGenerationData,
				prevMediaUrl: state.characterGenerationData.mediaUrl,
				mediaUrl: null,
				status: 'pending',
			};
		});
		builder.addCase(generateCharacterAsync.fulfilled, (state, action) => {
			state.characterGenerateApiStage = ApiStage.SUCCESS;
			const [mediaData] = action.payload.data?.media ?? [];
			const { media_id: mediaId } = mediaData ?? {};
			state.characterGenerationData = {
				mediaId,
				prevMediaUrl: state.characterGenerationData.thumbnail,
				mediaUrl: null,
				thumbnail: null,
				status: 'pending',
			};
		});
		builder.addCase(generateCharacterAsync.rejected, (state, action) => {
			state.characterGenerateApiStage = ApiStage.ERROR;
			const error = action.error.code;
			LoggerUtil.logError('[Generate Character Media]: Error', action?.error);
			LoggerUtil.logInfo('[Generate Character Media]: Parameters', action?.meta?.arg);

			if (error === RendernetErrors.NSFW_PROMPT) {
				captureAmplitudeEvent(AmplitudeEvents.NSFW_TOAST);
				capturePosthogEvent(PostHogEvents.NSFW_TOAST);
				sendToast(ToastType.NSFW_CONTENT);
			} else if (
				error !== RendernetErrors.INVALID_CREDENTIALS &&
				error !== RendernetErrors.BLOCKED_USER &&
				error !== RendernetErrors.INSUFFICIENT_CREDITS
			) {
				sendToast(ToastType.GENERATE_API_ERROR);
			}
			state.characterGenerationData = {
				...state.characterGenerationData,
				status: 'error',
			};
		});

		// Get character templates
		builder.addCase(getCharacterTemplatesAsync.pending, (state) => {
			state.characterTemplatesApiStage = ApiStage.LOADING;
		});
		builder.addCase(getCharacterTemplatesAsync.fulfilled, (state, action) => {
			state.characterTemplatesApiStage = ApiStage.SUCCESS;
			const { data } = action.payload;
			state.characterTemplates = data;
		});
		builder.addCase(getCharacterTemplatesAsync.rejected, (state, action) => {
			state.characterTemplatesApiStage = ApiStage.ERROR;
			const { error } = action;
			handleSomethingWentWrongErrorWithToast('API request failed', error);
		});

		// Get character description
		builder.addCase(getCharacterDescriptionAsync.pending, (state) => {
			state.characterDescriptionApiStage = ApiStage.LOADING;
		});
		builder.addCase(getCharacterDescriptionAsync.fulfilled, (state, action) => {
			state.characterDescriptionApiStage = ApiStage.SUCCESS;
			const { data } = action.payload;
			state.characterDescription = data.media?.[0]?.output;
		});
		builder.addCase(getCharacterDescriptionAsync.rejected, (state, action) => {
			state.characterDescriptionApiStage = ApiStage.ERROR;
			state.characterDescription = '';
			const { error } = action;
			if (error.code === RendernetErrors.FACE_NOT_FOUND) {
				sendToast(ToastType.CUSTOM, error.message);
			} else {
				handleSomethingWentWrongErrorWithToast('API request failed', error);
			}
		});

		createNewCharacterExtraReducers(builder);
	},
});

export const {
	setSelectedCharacterStudio,
	setSelectedCharacter,
	setSelectedCharacterHome,
	setControlWeightHome,
	setControlWeight,
	setControlWeightStudio,
	toggleEnableRealisticFaceSwapStudio,
	toggleEnableRealisticFaceSwapHome,
	toggleEnableRealisticFaceSwap,
	clearSelectedCharacter,
	clearSelectedCharacterHome,
	clearSelectedCharacterStudio,
	setCharacterPopUpData,

	setSelectedEditCharacter,

	resetCharacterCreationReferenceImage,
	setCharacterCreationReferenceImage,
	setCharacterGenerationPrompt,
	setCharacterGenerationData,
	setCroppedCharacterImage,
	setCharacterDescription,
	setCharacterDescriptionApiStage,
	resetCharacterState,
	setMyCharactersCurrentPage,
} = characterStudioSlice.actions;

export const selectedCharacterSelector = (state) => state.characterStudio.selectedCharacter;
export const studioSelectedCharacterSelector = (state) =>
	state.characterStudio.selectedCharacterStudio;
export const controlWeightSelector = (state) => state.characterStudio.controlWeight;
export const controlWeightSelectorStudio = (state) => state.characterStudio.controlWeightStudio;
export const controlWeightHomeSelector = (state) => state.characterStudio.controlWeightHome;
export const realisticFaceSwapSelector = (state) => state.characterStudio.realisticFaceSwap;
export const realisticFaceSwapSelectorStudio = (state) =>
	state.characterStudio.realisticFaceSwapStudio;
export const realisticFaceSwapHomeSelector = (state) => state.characterStudio.realisticFaceSwapHome;
export const listCharactersApiStageSelector = (state) =>
	state.characterStudio.listCharactersApiStage;
export const listCharactersSelector = (state) => state.characterStudio.listCharacters;
export const myCharactersCurrentPageSelector = (state) =>
	state.characterStudio.myCharactersCurrentPage;
export const characterPopUpDataSelector = (state) => state.characterStudio.characterPopUpData;

export const characterCreationReferenceImageSelector = (state) =>
	state.characterStudio.characterCreationReferenceImage;

export const selectedEditCharacterSelector = (state) => state.characterStudio.selectedEditCharacter;
export const homeSelectedCharacterSelector = (state) => state.characterStudio.selectedCharacterHome;
export const characterGenerationDataSelector = (state) =>
	state.characterStudio.characterGenerationData;
export const characterGenerationApiStageSelector = (state) =>
	state.characterStudio.characterGenerateApiStage;
export const characterDeleteApiStageSelector = (state) =>
	state.characterStudio.deleteCharacterApiStage;
export const characterEditApiStageSelector = (state) => state.characterStudio.editCharacterApiStage;

export const characterGenerationPromptSelector = (state) =>
	state.characterStudio.characterGenerationPrompt;
export const characterDescriptionApiStageSelector = (state) =>
	state.characterStudio.characterDescriptionApiStage;
export const characterDescriptionSelector = (state) => state.characterStudio.characterDescription;
export const characterTemplatesApiStageSelector = (state) =>
	state.characterStudio.characterTemplatesApiStage;
export const characterTemplatesSelector = (state) => state.characterStudio.characterTemplates;
export const croppedCharacterImageSelector = (state) => state.characterStudio.croppedCharacterImage;

export const handleCharacterAssetFromPusher = (newAsset) => async (dispatch, getState) => {
	const croppedCharacterImage = croppedCharacterImageSelector(getState());
	if (newAsset.status === 'active' && newAsset.id === croppedCharacterImage.id) {
		dispatch(setCroppedCharacterImage({ ...croppedCharacterImage, uploadStatus: true }));
	}
};

export default characterStudioSlice.reducer;
